var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, FieldSelect, FormField, FormGrid, ModalBody, ModalFoot, ModalHead, Text, } from '@dixa/lib-shared-habla';
import { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { useIsChanged } from 'components/powerFlowShared/hooks/useIsChanged';
import { hasFeature, IfFeatureFlagOn } from 'components/shared/featureFlag';
import { getRequestID } from 'components/shared/util/request';
import { CallPattern } from '../types';
import styles from './forward.module.scss';
import ForwardTable from './forward/forwardTable';
import { TIMEOUT_OPTIONS } from './forward/forwardTableRow';
const CALL_PATTERN_OPTIONS = [
    { id: CallPattern.FirstToPickUp, name: 'First to pick up' },
    { id: CallPattern.Sequence, name: 'Sequence' },
];
const DISPLAY_NUMBER_OPTIONS = [
    { id: 'b_number', name: 'The called number' },
    { id: 'a_number', name: "The caller's number" },
];
const getEmptyNumber = () => ({
    countryCode: null,
    id: getRequestID(),
    isValid: false,
    phoneNumber: null,
    sip: null,
    timeout: '20',
    type: 'phone',
    user: null,
});
const ForwardModalImpl = ({ triggerClose, replaceNode, node, nodeLabel, hasACforward, }) => {
    const { payload } = node;
    const initForward = useMemo(() => {
        var _a;
        const numbers = !((_a = payload === null || payload === void 0 ? void 0 : payload.numbers) === null || _a === void 0 ? void 0 : _a.length)
            ? [getEmptyNumber()]
            : payload.numbers.map((_a) => {
                var { timeout, isValid } = _a, number = __rest(_a, ["timeout", "isValid"]);
                const [{ id: firstTimeoutOption }] = TIMEOUT_OPTIONS;
                const isTimeoutValid = TIMEOUT_OPTIONS.some(({ id }) => id === timeout);
                return Object.assign(Object.assign({}, number), { 
                    /*
                     * This is for backwards compatibility.
                     * Since the "No timeout" (null) option was actually 10 minute option,
                     * we want to update the values to accurately reflect that.
                     */
                    timeout: timeout !== null && timeout !== void 0 ? timeout : firstTimeoutOption, isValid: isTimeoutValid && isValid });
            });
        if (!payload) {
            return {
                callPattern: CallPattern.FirstToPickUp,
                display_number: 'b_number',
                noAnswerNodeId: null,
                numbers,
                recordCall: true,
            };
        }
        const display_number = !payload.display_number || !hasACforward
            ? 'b_number'
            : payload.display_number;
        return Object.assign(Object.assign({}, payload), { display_number,
            numbers });
    }, [payload, hasACforward]);
    const [error, setError] = useState(null);
    const [forward, setForward] = useState(initForward);
    const isChanged = useIsChanged([initForward, forward]);
    const onCallPatternChange = useCallback((callPattern) => {
        setForward((forward) => {
            const state = Object.assign(Object.assign({}, forward), { callPattern });
            const isCallFirstToPickUp = callPattern === CallPattern.FirstToPickUp;
            if (!isCallFirstToPickUp) {
                return state;
            }
            const { numbers: stateNumbers } = state;
            const [{ timeout: firstNumberTimeout }] = stateNumbers;
            const numbers = stateNumbers.map((number) => (Object.assign(Object.assign({}, number), { timeout: firstNumberTimeout })));
            return Object.assign(Object.assign({}, state), { numbers });
        });
    }, [setForward]);
    const onDisplayNumberChange = useCallback((display_number) => {
        setForward((forward) => (Object.assign(Object.assign({}, forward), { display_number })));
    }, [setForward]);
    const onUpdateNumbers = useCallback((numbers) => {
        setForward((forward) => (Object.assign(Object.assign({}, forward), { numbers })));
    }, [setForward]);
    const onAddNumber = useCallback(() => {
        setForward((forward) => (Object.assign(Object.assign({}, forward), { numbers: [...forward.numbers, getEmptyNumber()] })));
    }, [setForward]);
    const onSave = useCallback(() => {
        // TODO: Check if all numbers are valid
        const { numbers, noAnswerNodeId } = forward;
        const isSomeNumberInvalid = numbers.some((number) => !number.isValid);
        if (isSomeNumberInvalid) {
            setError('One or more of the numbers are invalid.');
            return;
        }
        const numberWithoutTimeout = numbers.some((number) => !number.timeout);
        const payload = Object.assign(Object.assign({}, forward), { noAnswerNodeId: numberWithoutTimeout ? null : noAnswerNodeId || uuid() });
        setError(null);
        replaceNode(Object.assign(Object.assign({}, node), { icon: 'i-forward-to-phone', isAction: false, isEditable: true, isRemovable: true, nodeType: FlowNodeTypes.FORWARD, text: 'forward', payload }));
        triggerClose();
    }, [forward, node, setError, replaceNode, triggerClose]);
    const { callPattern = '', display_number = '' } = forward;
    return (_jsxs(Box, Object.assign({ width: "600px" }, { children: [_jsx(ModalHead, { title: nodeLabel, onClose: triggerClose }), _jsxs(ModalBody, { children: [_jsx(Text.Paragraph, Object.assign({ subdued: true }, { children: "Set up incoming calls to be forwarded to one or more phone numbers. The numbers can belong to your company (internal) or other companies (external)." })), _jsxs(FormGrid, Object.assign({ className: styles.form }, { children: [_jsx(FormField, { children: _jsx(FieldSelect, { label: "Call pattern", input: {
                                        name: 'callPatternSelect',
                                        value: callPattern,
                                        onChange: onCallPatternChange,
                                    }, items: CALL_PATTERN_OPTIONS, helpIcon: {
                                        title: 'Forward call pattern',
                                        text: (_jsxs(_Fragment, { children: ["Set the call pattern to ", _jsx("b", { children: "first agent to pick" }), " up or", ' ', _jsx("b", { children: "sequence" }), " (i.e. the first number on the list is called and if the call is not answered, then the second, and so on)"] })),
                                    }, required: true }) }), _jsx(IfFeatureFlagOn, Object.assign({ name: "a-c-forward" }, { children: _jsx(FormField, { children: _jsx(FieldSelect, { label: "Display number", input: {
                                            name: 'displayNumberSelect',
                                            value: display_number,
                                            onChange: onDisplayNumberChange,
                                        }, items: DISPLAY_NUMBER_OPTIONS, helpIcon: {
                                            title: 'Display number',
                                            text: `Select what you want the show as Caller ID. You can choose to show the caller’s number or the called number. This number will be presented to the recipient when activating the Forward Call action.`,
                                        }, required: true }) }) })), _jsx(FormField, { children: _jsx(ForwardTable, { forward: forward, onUpdateNumbers: onUpdateNumbers }) }), _jsx(FormField, Object.assign({ span: 2 }, { children: _jsx(Button, Object.assign({ variant: "secondary", onClick: onAddNumber }, { children: "Add number" })) }))] })), error && _jsx("p", Object.assign({ className: styles.error }, { children: error }))] }), _jsx(ModalFoot, { confirmOnClick: onSave, cancelOnClick: triggerClose, confirmProps: { disabled: !isChanged }, confirmName: getConfirmButtonVerb(node) })] })));
};
const mapStateToProps = (state) => ({
    hasACforward: hasFeature(state, 'a-c-forward'),
});
export const ForwardModal = connect(mapStateToProps)(ForwardModalImpl);
